import { type FC, type Ref } from 'react';
import { Flex, Heading, Input, Section, Text } from '@highereducation/hen-ui';
import type { PiiFormSectionTypes } from './types';
import { formatPhoneNumber } from '@de/utils/formatPhoneNumber';
import { useDynamicExperienceContext } from '../../providers/DynamicExperienceProvider';
import { useTracking } from '../../providers/TrackingProvider';
import { createResultsEventContext } from '../../utils/createResultsEventContext';
import {
  createFormViewedPayload,
  createFieldEventsPayload,
} from '../../utils/createEventingPayloadsDE';
import useViewEvents from '../../hooks/useViewEvents';
import { useDebouncedFieldEvent } from './hooks/useDebouncedFieldEvent';
import { ErrorMessage } from '@de/components/ErrorMessage';

// The four PII fields rendered by this confirmation form. Field names/types
// mirror the `sendDebouncedFieldEvent` calls below so `FieldViewed` and
// `FieldInputted` events stay aligned for the same field.
const PII_FORM_FIELDS = [
  {
    fieldName: 'first-name',
    fieldType: 'text',
    fieldLabel: 'First Name',
    fieldId: 'first-name',
  },
  {
    fieldName: 'last-name',
    fieldType: 'text',
    fieldLabel: 'Last Name',
    fieldId: 'last-name',
  },
  {
    fieldName: 'email',
    fieldType: 'email',
    fieldLabel: 'Email',
    fieldId: 'email',
  },
  {
    fieldName: 'phone',
    fieldType: 'text',
    fieldLabel: 'Phone',
    fieldId: 'phone',
  },
] as const;

const PiiFormSection: FC<PiiFormSectionTypes> = ({
  header = 'Confirm your contact details:',
  firstName,
  lastName,
  email,
  phone,
  setFirstName,
  setLastName,
  setEmail,
  setPhone,
  onSubmit,
  formErrors,
  showPhoneError = false,
  showEmailError = false,
  productClickedCorrelationId,
  dslConfigId,
}) => {
  // `dslConfigId` is threaded for consistency with the updated hook
  // signature. The PII `FormViewed` and `FieldViewed`/`FieldInputted` events
  // all omit it on the wire: their registered Tagular schemas do not define a
  // top-level `customDimensions` and reject it with HTTP 400. The lead funnel
  // still correlates to the config via the `FormSubmitted`/product dimensions
  // and the shared `correlationId`.
  const { sendDebouncedFieldEvent } = useDebouncedFieldEvent(
    productClickedCorrelationId,
    dslConfigId,
  );
  const { eventContext } = useDynamicExperienceContext();
  const publishedVersion = useTracking()?.stepCorrelation.dslConfigVersion;
  const resultEventContext = {
    ...createResultsEventContext({
      eventContext: eventContext,
      productClickedCorrelationId: productClickedCorrelationId,
      dslConfigId,
      dslConfigVersion: dslConfigId ? publishedVersion : undefined,
    }),
    dslConfigId,
  };

  // The PII confirmation form renders four input fields. Each one is a
  // distinct tracked field, so we emit one `FieldViewed` per field (matching
  // the per-field `FieldInputted` naming in `sendDebouncedFieldEvent`) rather
  // than a single aggregate event for the whole form.
  const { ref: formViewedRef } = useViewEvents([
    createFormViewedPayload(resultEventContext, true),
    ...PII_FORM_FIELDS.map((field) =>
      createFieldEventsPayload(
        {
          ...resultEventContext,
          fieldName: field.fieldName,
          fieldType: field.fieldType,
          fieldLabel: field.fieldLabel,
          fieldId: field.fieldId,
        },
        'FieldViewed',
      ),
    ),
  ]);

  return (
    <Section
      as='section'
      padding='none'
      className='not-prose gap-0'
      ref={formViewedRef as Ref<HTMLDivElement>}
    >
      <Flex direction='row' align='center' justify='between' gap='none'>
        <Heading variant='h5' className='mb-3 mt-4'>
          {header}
        </Heading>
      </Flex>

      {formErrors?.length !== 0 && (
        <>
          <Text tag='p' variant='b5' className='my-1 text-error-500'>
            We were unable to save your information. Please correct the errors
            listed below.
          </Text>
          {formErrors?.map((obj) => (
            <ErrorMessage
              key={obj.field}
              message={`Make sure the ${obj.field} field is valid.`}
            />
          ))}
        </>
      )}

      <Flex asChild direction='column' gap='sm'>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <Flex direction='column' gap='none' className='flex-1'>
            <Input
              variant='two-line'
              label='First Name'
              id='first-name'
              type='text'
              data-testid='pii-form-first-name-input'
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                sendDebouncedFieldEvent('first-name', 'text');
              }}
            />
          </Flex>
          <Flex direction='column' gap='none' className='flex-1'>
            <Input
              variant='two-line'
              label='Last Name'
              id='last-name'
              type='text'
              data-testid='pii-form-last-name-input'
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                sendDebouncedFieldEvent('last-name', 'text');
              }}
            />
          </Flex>

          <Flex direction='column' gap='none'>
            <Input
              variant='two-line'
              label='Email'
              id='email'
              type='email'
              data-testid='pii-form-email-input'
              value={email}
              error={showEmailError}
              onChange={(e) => {
                setEmail(e.target.value);
                sendDebouncedFieldEvent('email', 'email');
              }}
            />
            {showEmailError && (
              <Flex
                direction='row'
                justify='start'
                width='full'
                gap='none'
                className='pt-1'
              >
                <ErrorMessage message='Please enter a valid email address.' />
              </Flex>
            )}
          </Flex>

          <Flex direction='column' gap='none'>
            <Input
              variant='two-line'
              label='Phone'
              id='phone'
              type='text'
              inputMode='numeric'
              autoComplete='tel'
              maxLength={14}
              data-testid='pii-form-phone-input'
              value={phone}
              error={showPhoneError}
              onChange={(e) => {
                // Format to `(999) 999-9999` on every keystroke via the shared
                // helper (single source of truth with the in-flow phone
                // question, TextQuestion). Replaces the React-19-incompatible
                // `react-input-mask`; stored value stays the masked string.
                setPhone(formatPhoneNumber(e.target.value));
                sendDebouncedFieldEvent('phone', 'text');
              }}
            />
            {showPhoneError && (
              <Flex
                direction='row'
                justify='start'
                width='full'
                gap='none'
                className='pt-1'
              >
                <ErrorMessage message='Please enter a valid phone number.' />
              </Flex>
            )}
          </Flex>
        </form>
      </Flex>
    </Section>
  );
};

export default PiiFormSection;
